import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Star, 
  TrendingUp, 
  Users, 
  MessageSquare, 
  Calendar,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  Clock,
  Loader,
  XCircle
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import api from '../utils/api';
import './DashboardLegacy.css';

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalReviews: 0,
    averageRating: 0,
    totalBranches: 0,
    recentReviews: 0
  });
  
  const [sentimentData, setSentimentData] = useState([]);
  const [ratingDistribution, setRatingDistribution] = useState([]);
  const [ratingCalendar, setRatingCalendar] = useState([]);
  const [emotionData, setEmotionData] = useState([]);
  const [bestComments, setBestComments] = useState([]);
  const [worstComments, setWorstComments] = useState([]);
  const [loading, setLoading] = useState(true);
  // Removed branch selection - always show latest from all branches
  const [, setBranches] = useState([]); // Keep for branch count display
  // Day modal states for calendar interaction
  const [, setSelectedDay] = useState(null);
  const [dayModalData, setDayModalData] = useState(null);
  // Cron pipeline status
  const [cronStatus, setCronStatus] = useState(null);
  
  // Frontend caching state - using localStorage for persistence
  const [cacheTimestamp, setCacheTimestamp] = useState(() => {
    try {
      const stored = localStorage.getItem('dashboard-cache-timestamp');
      return stored ? parseInt(stored) : null;
    } catch (error) {
      console.warn('localStorage not available:', error);
      return null;
    }
  });
  const [cachedData, setCachedData] = useState(() => {
    try {
      const stored = localStorage.getItem('dashboard-cached-data');
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.warn('localStorage not available:', error);
      return null;
    }
  });
  

  useEffect(() => {
    // Check if we have cached data that's still fresh (less than 6 hours old)
    const now = Date.now();
    const cacheAge = cacheTimestamp ? now - cacheTimestamp : Infinity;
    const CACHE_DURATION = 6 * 60 * 60 * 1000; // 6 hours (since reviews update daily)
    
    if (cachedData && cacheAge < CACHE_DURATION) {
      console.log('🚀 Using cached dashboard data (age:', Math.round(cacheAge / (60 * 60 * 1000)), 'hours)');
      setLoading(false);
      
      // Restore cached data
      setStats(cachedData.stats);
      setSentimentData(cachedData.sentimentData);
      setRatingDistribution(cachedData.ratingDistribution);
      setRatingCalendar(cachedData.ratingCalendar);
      setEmotionData(cachedData.emotionData);
      setBestComments(cachedData.bestComments);
      setWorstComments(cachedData.worstComments);
      setBranches(cachedData.branches);
    } else {
      console.log('🔄 Cache expired or missing, fetching fresh data...');
      fetchDashboardData();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Remove complex branch selection - always fetch latest data
  // useEffect(() => {
  //   if (selectedBranches.length > 0) {
  //     fetchDashboardData();
  //   }
  // }, [selectedBranches]);

  // fetchBranches is now inline in useEffect

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Collect all data first, then set states and cache
      let allData = {
        stats: { totalReviews: 0, averageRating: 0, totalBranches: 0, recentReviews: 0 },
        sentimentData: [],
        ratingDistribution: [],
        ratingCalendar: [],
        emotionData: [],
        bestComments: [],
        worstComments: [],
        branches: []
      };
      
      // Fetch branches count first
      let branchCount = 0;
      try {
        const branchesResponse = await api.get('/api/branches');
        if (branchesResponse.data.success) {
          branchCount = branchesResponse.data.branches.length;
          allData.branches = branchesResponse.data.branches;
          console.log(`📊 Loaded ${branchCount} branches`);
        }
      } catch (error) {
        console.error('Error fetching branches:', error);
      }
      
      // Always fetch stats from all branches - no complex filtering
      const statsResponse = await api.get('/api/reviews/stats');
      
      if (statsResponse.data.success) {
        const dbStats = statsResponse.data.stats;
        
        allData.stats = {
          totalReviews: dbStats.totalReviews,
          averageRating: parseFloat(dbStats.averageRating),
          totalBranches: branchCount, // Use fetched branch count
          recentReviews: dbStats.totalReviews
        };
        
        // Generate rating distribution from database
        allData.ratingDistribution = [
          { rating: '5★', count: dbStats.ratingDistribution[5] || 0, color: '#10B981' },
          { rating: '4★', count: dbStats.ratingDistribution[4] || 0, color: '#34D399' },
          { rating: '3★', count: dbStats.ratingDistribution[3] || 0, color: '#FBBF24' },
          { rating: '2★', count: dbStats.ratingDistribution[2] || 0, color: '#F59E0B' },
          { rating: '1★', count: dbStats.ratingDistribution[1] || 0, color: '#EF4444' }
        ];
        
        // Fetch AI analysis data for emotions and sentiment
        const aiData = await fetchAIAnalysisDataForCache();
        allData.emotionData = aiData.emotionData;
        allData.bestComments = aiData.bestComments;
        allData.worstComments = aiData.worstComments;
        allData.sentimentData = aiData.sentimentData;
        
        // Fetch rating calendar data (GitHub-style calendar)
        allData.ratingCalendar = await fetchRatingCalendarForCache();
      } else {
        // Fallback to sample data if database is not available
        allData.stats = {
          totalReviews: 0,
          averageRating: 0,
          totalBranches: 0,
          recentReviews: 0
        };
      }
      
      // Generate sample sentiment data (in real app, this would come from AI analysis)
      if (allData.sentimentData.length === 0) {
        allData.sentimentData = [
          { name: 'Positive', value: Math.floor(Math.random() * 60) + 30, color: '#10B981' },
          { name: 'Neutral', value: Math.floor(Math.random() * 30) + 15, color: '#6B7280' },
          { name: 'Negative', value: Math.floor(Math.random() * 20) + 5, color: '#EF4444' }
        ];
      }
      
      // Set all states at once
      console.log('📊 Setting states with data:', {
        emotionData: allData.emotionData,
        sentimentData: allData.sentimentData,
        bestComments: allData.bestComments?.length,
        worstComments: allData.worstComments?.length
      });
      
      setStats(allData.stats);
      setSentimentData(allData.sentimentData);
      setRatingDistribution(allData.ratingDistribution);
      setRatingCalendar(allData.ratingCalendar);
      setEmotionData(allData.emotionData);
      setBestComments(allData.bestComments);
      setWorstComments(allData.worstComments);
      setBranches(allData.branches);
      
      // Cache the complete data
      setCachedData(allData);
      setCacheTimestamp(Date.now());
      
      // Save to localStorage for persistence across navigation
      try {
        localStorage.setItem('dashboard-cached-data', JSON.stringify(allData));
        localStorage.setItem('dashboard-cache-timestamp', Date.now().toString());
        console.log('💾 Complete dashboard data cached for future use (persistent)');
      } catch (error) {
        console.warn('Failed to save to localStorage:', error);
        console.log('💾 Dashboard data cached for current session only');
      }
      
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      // Fallback to sample data
      setStats({
        totalReviews: 0,
        averageRating: 0,
        totalBranches: 0,
        recentReviews: 0
      });
    } finally {
      setLoading(false);
    }
  };

  // Manual refresh function to force fetch fresh data
  const refreshDashboard = async () => {
    console.log('🔄 Manual refresh requested - clearing cache and fetching fresh data');
    setCachedData(null);
    setCacheTimestamp(null);
    
    // Clear localStorage cache
    try {
      localStorage.removeItem('dashboard-cached-data');
      localStorage.removeItem('dashboard-cache-timestamp');
    } catch (error) {
      console.warn('Failed to clear localStorage:', error);
    }
    
    await fetchDashboardData();
  };

  // New function that returns calendar data instead of setting state
  const fetchRatingCalendarForCache = async () => {
    try {
      // Use dedicated calendar endpoint with higher rate limits
      const response = await api.get('/api/reviews/calendar?limit=50000');
      
      if (response.data.success) {
        const reviews = response.data.reviews;
        console.log(`📅 Fetched ${reviews.length} reviews for calendar`);
        
        // Generate calendar data for the last 365 days
        const calendarData = generateCalendarData(reviews);
        console.log(`📅 Generated calendar with ${calendarData.length} days`);
        return calendarData;
      }
    } catch (error) {
      console.error('Error fetching rating calendar:', error);
      // Fallback to regular reviews endpoint if calendar endpoint fails
      try {
        const fallbackResponse = await api.get('/api/reviews?limit=5000');
        if (fallbackResponse.data.success) {
          const reviews = fallbackResponse.data.reviews;
          console.log(`📅 Fallback: Fetched ${reviews.length} reviews for calendar`);
          
          const calendarData = generateCalendarData(reviews);
          console.log(`📅 Fallback: Generated calendar with ${calendarData.length} days`);
          return calendarData;
        }
      } catch (fallbackError) {
        console.error('Fallback calendar fetch also failed:', fallbackError);
      }
    }
    return [];
  };

  // New function that returns AI data instead of setting state
  const fetchAIAnalysisDataForCache = async () => {
    try {
      console.log('📊 Fetching latest AI analysis data for Dashboard...');
      
      const response = await api.get('/api/dashboard/analytics');
      
      if (response.data.success) {
        const analytics = response.data.analytics;
        
        console.log('📊 AI Analytics data received:', {
          emotionData: analytics.emotionData?.length || 0,
          sentimentData: analytics.sentimentData?.length || 0,
          bestComments: analytics.bestComments?.length || 0,
          worstComments: analytics.worstComments?.length || 0
        });
        
        console.log('📊 Raw emotion data:', analytics.emotionData);
        console.log('📊 Raw sentiment data:', analytics.sentimentData);
        
        // Convert emotion data to chart format with colors
        const emotionColors = {
          joy: '#10B981',           // Green
          satisfaction: '#34D399',  // Light Green
          excitement: '#8B5CF6',    // Purple
          contentment: '#06B6D4',   // Cyan
          frustration: '#F59E0B',   // Orange
          anger: '#EF4444',         // Red
          disappointment: '#F97316', // Dark Orange
          concern: '#FBBF24',       // Yellow
          neutral: '#6B7280'        // Gray
        };
        
        const emotionChartData = (analytics.emotionData || []).map(item => ({
          name: item.emotion.charAt(0).toUpperCase() + item.emotion.slice(1),
          value: item.count,
          percentage: item.percentage,
          color: emotionColors[item.emotion] || '#6B7280'
        }));
        
        // Convert sentiment data to chart format with colors
        const sentimentColors = {
          positive: '#10B981',  // Green
          negative: '#EF4444',  // Red
          neutral: '#6B7280'    // Gray
        };
        
        const sentimentChartData = (analytics.sentimentData || []).map(item => ({
          name: item.sentiment.charAt(0).toUpperCase() + item.sentiment.slice(1),
          value: item.count,
          percentage: item.percentage,
          color: sentimentColors[item.sentiment] || '#6B7280'
        }));
        
        console.log('📊 Processed emotion chart data:', emotionChartData);
        console.log('📊 Processed sentiment chart data:', sentimentChartData);
        
        return {
          emotionData: emotionChartData,
          sentimentData: sentimentChartData,
          bestComments: analytics.bestComments || [],
          worstComments: analytics.worstComments || []
        };
      } else {
        console.warn('📊 AI Analytics response not successful:', response.data);
        return {
          emotionData: [],
          sentimentData: [],
          bestComments: [],
          worstComments: []
        };
      }
    } catch (error) {
      console.error('❌ Error fetching Dashboard analytics:', error);
      return {
        emotionData: [],
        sentimentData: [],
        bestComments: [],
        worstComments: []
      };
    }
  };

  // eslint-disable-next-line no-unused-vars
  const fetchRatingCalendar = async () => {
    try {
      // Use dedicated calendar endpoint with higher rate limits
      const response = await api.get('/api/reviews/calendar?limit=50000');
      
      if (response.data.success) {
        const reviews = response.data.reviews;
        console.log(`📅 Fetched ${reviews.length} reviews for calendar`);
        
        // Generate calendar data for the last 365 days
        const calendarData = generateCalendarData(reviews);
        setRatingCalendar(calendarData);
        
        console.log(`📅 Generated calendar with ${calendarData.length} days`);
      }
    } catch (error) {
      console.error('Error fetching rating calendar:', error);
      // Fallback to regular reviews endpoint if calendar endpoint fails
      try {
        const fallbackResponse = await api.get('/api/reviews?limit=5000');
        if (fallbackResponse.data.success) {
          const reviews = fallbackResponse.data.reviews;
          const calendarData = generateCalendarData(reviews);
          setRatingCalendar(calendarData);
          console.log(`📅 Used fallback dataset with ${reviews.length} reviews`);
        }
      } catch (fallbackError) {
        console.error('Fallback also failed:', fallbackError);
      }
    }
  };

  const generateCalendarData = (reviews) => {
    const today = new Date();
    const days = [];
    
    // Create array of last 365 days from today
    for (let i = 364; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      
      const dateStr = date.toISOString().split('T')[0];
      const dayReviews = reviews.filter(review => 
        review.published_at_date && review.published_at_date.startsWith(dateStr)
      );
      
      const ratingCounts = {
        1: dayReviews.filter(r => r.stars === 1).length,
        2: dayReviews.filter(r => r.stars === 2).length,
        3: dayReviews.filter(r => r.stars === 3).length,
        4: dayReviews.filter(r => r.stars === 4).length,
        5: dayReviews.filter(r => r.stars === 5).length,
      };
      
      const totalReviews = dayReviews.length;
      const avgRating = totalReviews > 0 
        ? dayReviews.reduce((sum, r) => sum + r.stars, 0) / totalReviews 
        : 0;
      
      // Debug logging for days with reviews
      if (totalReviews > 0) {
        console.log(`📅 ${dateStr}: ${totalReviews} reviews, avg rating: ${avgRating.toFixed(1)}`);
      }
      
      // Determine color based on average rating, intensity based on review count
      let colorType = 'none';
      let intensity = 0;
      
      if (totalReviews > 0) {
        // Determine intensity based on number of reviews
        if (totalReviews >= 1) intensity = 1;
        if (totalReviews >= 3) intensity = 2;
        if (totalReviews >= 6) intensity = 3;
        if (totalReviews >= 10) intensity = 4;
        
        // Determine color based on average rating
        if (avgRating >= 4.5) colorType = 'excellent'; // Green for 4.5-5 stars
        else if (avgRating >= 3.5) colorType = 'good';      // Light green for 3.5-4.4 stars
        else if (avgRating >= 2.5) colorType = 'average';   // Yellow for 2.5-3.4 stars
        else if (avgRating >= 1.5) colorType = 'poor';      // Orange for 1.5-2.4 stars
        else colorType = 'bad';                              // Red for 1-1.4 stars
      }
      
      days.push({
        date: dateStr,
        day: date,
        totalReviews,
        avgRating,
        ratingCounts,
        intensity,
        colorType
      });
    }
    
    // Summary logging
    const daysWithReviews = days.filter(day => day.totalReviews > 0);
    console.log(`📅 Calendar generated: ${days.length} total days, ${daysWithReviews.length} days with reviews`);
    console.log(`📅 Date range: ${days[0]?.date} to ${days[days.length - 1]?.date}`);
    
    return days;
  };

  // eslint-disable-next-line no-unused-vars
  const fetchAIAnalysisData = async () => {
    try {
      console.log('📊 Fetching latest AI analysis data for Dashboard...');
      
      // Always fetch latest data from all branches - no complex branch selection
      const analyticsResponse = await api.get('/api/dashboard/analytics');
      
      if (analyticsResponse.data.success) {
        const analytics = analyticsResponse.data.analytics || {};
        
        console.log(`📊 Fetched latest analytics for ${analytics.totalAnalyses} analyses`);
        
        // Use pre-processed data from the server
        const emotionData = analytics.emotionData || [];
        const sentimentData = analytics.sentimentData || [];
        const bestComments = analytics.bestComments || [];
        const worstComments = analytics.worstComments || [];
        
        // Convert emotion data to chart format with colors
        const emotionColors = {
          joy: '#10B981',           // Green
          satisfaction: '#34D399',  // Light Green
          excitement: '#8B5CF6',    // Purple
          contentment: '#06B6D4',   // Cyan
          frustration: '#F59E0B',   // Orange
          anger: '#EF4444',         // Red
          disappointment: '#F97316', // Dark Orange
          concern: '#FBBF24',       // Yellow
          neutral: '#6B7280'        // Gray
        };
        
        const emotionChartData = emotionData.map(item => ({
          name: item.emotion.charAt(0).toUpperCase() + item.emotion.slice(1),
          value: item.count,
          percentage: item.percentage,
          color: emotionColors[item.emotion] || '#6B7280'
        }));
        
        // Convert sentiment data to chart format with colors
        const sentimentColors = {
          positive: '#10B981',  // Green
          negative: '#EF4444',  // Red
          neutral: '#6B7280'    // Gray
        };
        
        const sentimentChartData = sentimentData.map(item => ({
          name: item.sentiment.charAt(0).toUpperCase() + item.sentiment.slice(1),
          value: item.count,
          percentage: item.percentage,
          color: sentimentColors[item.sentiment] || '#6B7280'
        }));
        
        // Set the processed data
        setEmotionData(emotionChartData);
        setSentimentData(sentimentChartData);
        
        // Show only latest 3 best comments and 3 worst comments
        setBestComments(bestComments.slice(0, 3));
        setWorstComments(worstComments.slice(0, 3));
        
        console.log(`📊 Dashboard data processed: ${emotionChartData.length} emotions, ${sentimentChartData.length} sentiments, 3 best comments, 3 worst comments`);
        
      } else {
        console.error('❌ Failed to fetch Dashboard analytics:', analyticsResponse.data.error);
        // Fallback to empty data
        setEmotionData([]);
        setSentimentData([]);
        setBestComments([]);
        setWorstComments([]);
      }
      
    } catch (error) {
      console.error('❌ Error fetching Dashboard analytics:', error);
      // Fallback to empty data
      setEmotionData([]);
      setSentimentData([]);
      setBestComments([]);
      setWorstComments([]);
    }
  };

  // Removed unused color functions
  // const getEmotionColor = (emotion) => { ... }
  // const getSentimentColor = (sentiment) => { ... }

  // Removed branch selection functions - no longer needed

  // Fetch cron pipeline status
  useEffect(() => {
    fetchCronStatus();
    // Refresh cron status every 2 minutes
    const interval = setInterval(fetchCronStatus, 120000);
    return () => clearInterval(interval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchCronStatus = async () => {
    try {
      const response = await api.get('/api/services/status');
      if (response.data.success) {
        setCronStatus(response.data);
      }
    } catch (error) {
      // Silently fail - cron status is supplementary info
      console.log('Cron status not available:', error.message);
    }
  };

  const handleDayClick = (dayData) => {
    setSelectedDay(dayData.date);
    setDayModalData(dayData);
  };

  const StatCard = ({ icon: Icon, title, value, subtitle }) => (
    <motion.div 
      className="stat-card glass-effect hover-glow"
      whileHover={{ scale: 1.02 }}
      transition={{ duration: 0.2 }}
    >
      <div className="stat-icon">
        <Icon size={24} />
      </div>
      <div className="stat-content">
        <h3 className="stat-value">{value}</h3>
        <p className="stat-title">{title}</p>
        {subtitle && <p className="stat-subtitle">{subtitle}</p>}
      </div>
    </motion.div>
  );

  if (loading) {
    return (
      <div className="dashboard-loading">
        <RefreshCw className="loading-icon pulse" size={48} />
        <p>Loading dashboard data...</p>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <div className="header-content">
          <h1 className="dashboard-title">Analytics Dashboard</h1>
          <p className="dashboard-subtitle">
            Monitor your Google Maps reviews performance
            {cachedData && cacheTimestamp && (
              <span className="cache-status">
                {' • '}
                <span className="cache-indicator">
                  📦 Cached {Math.round((Date.now() - cacheTimestamp) / (60 * 60 * 1000))}h ago
                </span>
              </span>
            )}
          </p>
        </div>
        
        <div className="header-controls">
          {/* Refresh button for manual cache refresh */}
          <button 
            className="refresh-btn glass-effect hover-glow"
            onClick={refreshDashboard}
            disabled={loading}
            title="Refresh dashboard data"
          >
            <RefreshCw className={`refresh-icon ${loading ? 'spinning' : ''}`} size={16} />
            Refresh Data
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <StatCard 
          icon={MessageSquare} 
          title="Total Reviews" 
          value={stats.totalReviews.toLocaleString()} 
          subtitle="Across the branch"
        />
        <StatCard 
          icon={Star} 
          title="Average Rating" 
          value={stats.averageRating} 
          subtitle="Out of 5 stars"
        />
        <StatCard 
          icon={Users} 
          title="Total Branches" 
          value={stats.totalBranches} 
          subtitle="Being monitored"
        />
        <StatCard 
          icon={TrendingUp} 
          title="Recent Activity" 
          value={stats.recentReviews} 
          subtitle="Reviews this month"
        />
      </div>

      {/* Cron Pipeline Status */}
      {cronStatus && cronStatus.jobs && cronStatus.jobs.length > 0 && (
        <div className="cron-status-panel glass-effect">
          <h3 className="section-title">
            <Clock size={20} />
            Today's Automated Pipeline
            <span className="cron-date">{cronStatus.date}</span>
          </h3>
          <div className="cron-summary">
            <span className="cron-stat completed"><CheckCircle size={14} /> {cronStatus.summary?.completed || 0} completed</span>
            <span className="cron-stat in-progress"><Loader size={14} /> {(cronStatus.summary?.scraping || 0) + (cronStatus.summary?.fetching || 0) + (cronStatus.summary?.analyzing || 0)} in progress</span>
            <span className="cron-stat pending"><Clock size={14} /> {cronStatus.summary?.pending || 0} pending</span>
            {cronStatus.summary?.failed > 0 && (
              <span className="cron-stat failed"><XCircle size={14} /> {cronStatus.summary.failed} failed</span>
            )}
          </div>
          <div className="cron-jobs-list">
            {cronStatus.jobs.map(job => (
              <div key={job.id} className={`cron-job-item status-${job.status}`}>
                <span className="job-branch">{job.branches?.name || 'Unknown Branch'}</span>
                <span className={`job-status badge-${job.status}`}>{job.status}</span>
                <span className="job-reviews">
                  {job.reviews_found > 0 && `${job.reviews_saved || 0}/${job.reviews_found} saved`}
                  {job.reviews_analyzed > 0 && ` · ${job.reviews_analyzed} analyzed`}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Charts Section */}
      <div className="charts-section">
        <div className="chart-container glass-effect">
          <h3 className="chart-title">Rating Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={ratingDistribution}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
              <XAxis dataKey="rating" stroke="rgba(255,255,255,0.7)" />
              <YAxis stroke="rgba(255,255,255,0.7)" />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'rgba(0,0,0,0.9)', 
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '8px'
                }}
              />
              <Bar dataKey="count" fill="#ffffff" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="pie-charts-row">
          <div className="pie-chart-container glass-effect">
            <h3 className="chart-title">Sentiment Analysis</h3>
            {console.log('📊 Rendering sentiment chart with data:', sentimentData)}
            {sentimentData.length === 0 ? (
              <div className="no-data-message">
                <p>No sentiment data available</p>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={sentimentData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent, value }) => 
                    percent > 0.05 ? `${name} ${(percent * 100).toFixed(0)}%` : ''
                  }
                  outerRadius={60}
                  innerRadius={20}
                  dataKey="value"
                  nameKey="name"
                >
                  {sentimentData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value, name, props) => [
                    `${value} reviews (${props.payload.percentage}%)`,
                    name
                  ]}
                  contentStyle={{ 
                    backgroundColor: 'rgba(0,0,0,0.9)', 
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '12px'
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
            )}
          </div>

          <div className="pie-chart-container glass-effect">
            <h3 className="chart-title">Customer Emotions</h3>
            {console.log('📊 Rendering emotion chart with data:', emotionData)}
            {emotionData.length === 0 ? (
              <div className="no-data-message">
                <p>No emotion data available</p>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={emotionData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent, value }) => 
                    percent > 0.05 ? `${name} ${(percent * 100).toFixed(0)}%` : ''
                  }
                  outerRadius={60}
                  innerRadius={20}
                  dataKey="value"
                  nameKey="name"
                >
                  {emotionData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value, name, props) => [
                    `${value} reviews (${props.payload.percentage}%)`,
                    name
                  ]}
                  contentStyle={{ 
                    backgroundColor: 'rgba(0,0,0,0.9)', 
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '12px'
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      {/* AI Analysis Widgets - Always show latest 3 best and 3 worst comments */}
      <div className="ai-widgets-section">
          {/* Best Comments */}
          <div className="ai-widget glass-effect">
            <h3 className="widget-title">
              <Star size={20} />
              Latest Positive Reviews
            </h3>
            <div className="comments-list">
              {bestComments.length > 0 ? (
                bestComments.map((comment, index) => (
                  <div key={index} className="comment-item positive">
                    <div className="comment-header">
                      <span className="reviewer-name">{comment.reviewer || 'Anonymous'}</span>
                      {comment.branchName && (
                        <span className="branch-tag">{comment.branchName}</span>
                      )}
                    </div>
                    <p className="comment-text">{comment.text}</p>
                    <span className="comment-date">{new Date(comment.date).toLocaleDateString()}</span>
                  </div>
                ))
              ) : (
                <p className="no-data">No positive comments yet</p>
              )}
            </div>
          </div>

          {/* Worst Comments */}
          <div className="ai-widget glass-effect">
            <h3 className="widget-title">
              <AlertTriangle size={20} />
              Latest Negative Reviews
            </h3>
            <div className="comments-list">
              {worstComments.length > 0 ? (
                worstComments.map((comment, index) => (
                  <div key={index} className={`comment-item negative ${comment.urgency ? 'urgent' : ''}`}>
                    <div className="comment-header">
                      <span className="reviewer-name">{comment.reviewer || 'Anonymous'}</span>
                      {comment.branchName && (
                        <span className="branch-tag">{comment.branchName}</span>
                      )}
                    </div>
                    <p className="comment-text">{comment.text}</p>
                    <span className="comment-date">{new Date(comment.date).toLocaleDateString()}</span>
                  </div>
                ))
              ) : (
                <p className="no-data">No negative comments yet</p>
              )}
            </div>
          </div>
        </div>

      {/* Rating Calendar */}
      <div className="rating-calendar glass-effect">
        <h3 className="section-title">
          <Calendar size={20} />
          Reviews Activity Timeline
        </h3>
        
        <p className="calendar-subtitle">
          Last 365 days • Colors show average rating quality for each day
        </p>
        
        <div className="calendar-container">
          <div className="calendar-wrapper">
            <div className="calendar-grid">
              {ratingCalendar.map((day, index) => (
                <motion.div
                  key={day.date}
                  className={`calendar-day color-${day.colorType} intensity-${day.intensity}`}
                  onClick={() => handleDayClick(day)}
                  title={`${day.date}: ${day.totalReviews} reviews, avg ${day.avgRating.toFixed(1)}★`}
                  whileHover={{ scale: 1.2 }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: index * 0.001 }}
                />
              ))}
            </div>
          </div>
          
          <div className="calendar-legends">
            {/* Rating Quality Legend */}
            <div className="legend-section">
              <span className="legend-title">Rating Quality:</span>
              <div className="legend-items">
                <div className="legend-item">
                  <div className="legend-square color-excellent intensity-2" />
                  <span>Excellent (4.5-5★)</span>
                </div>
                <div className="legend-item">
                  <div className="legend-square color-good intensity-2" />
                  <span>Good (3.5-4.4★)</span>
                </div>
                <div className="legend-item">
                  <div className="legend-square color-average intensity-2" />
                  <span>Average (2.5-3.4★)</span>
                </div>
                <div className="legend-item">
                  <div className="legend-square color-poor intensity-2" />
                  <span>Poor (1.5-2.4★)</span>
                </div>
                <div className="legend-item">
                  <div className="legend-square color-bad intensity-2" />
                  <span>Bad (1-1.4★)</span>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
      
      {/* Day Details Modal */}
      {dayModalData && (
        <motion.div 
          className="day-modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setDayModalData(null)}
        >
          <motion.div 
            className="day-modal glass-effect"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            onClick={e => e.stopPropagation()}
          >
            <div className="modal-header">
              <h4>Reviews for {dayModalData.date}</h4>
              <button onClick={() => setDayModalData(null)}>×</button>
            </div>
            
            <div className="modal-content">
              <div className="day-stats">
                <div className="stat">
                  <span className="label">Total Reviews:</span>
                  <span className="value">{dayModalData.totalReviews}</span>
                </div>
                <div className="stat">
                  <span className="label">Average Rating:</span>
                  <span className="value">{dayModalData.avgRating.toFixed(1)}★</span>
                </div>
              </div>
              
              <div className="rating-breakdown">
                <h5>Rating Breakdown:</h5>
                {[5, 4, 3, 2, 1].map(star => (
                  <div key={star} className="rating-row">
                    <span className="stars">{star}★</span>
                    <div className="rating-bar">
                      <div 
                        className="rating-fill"
                        style={{ 
                          width: `${dayModalData.totalReviews > 0 ? (dayModalData.ratingCounts[star] / dayModalData.totalReviews) * 100 : 0}%`,
                          backgroundColor: star >= 4 ? '#10B981' : star === 3 ? '#FBBF24' : '#EF4444'
                        }}
                      />
                    </div>
                    <span className="count">{dayModalData.ratingCounts[star]}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default Dashboard;
